import { Dialog } from '@mui/material';
import { Link } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';

import { InlineMessage } from '@components/InlineMessage';
import DisableEnableUserContent from '@components/Modal/Content/DisableEnableUserModalContent/DisableEnableUserContent';

import {
  HANDOFF_FAILED,
  LOGGED_OUT,
  SESSION_EXPIRED,
} from '@constants/error-codes';
import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import AuthLayout, { BottomText } from '@layouts/AuthLayout';

import LoginForm from './LoginForm';

const messages = defineMessages({
  logIn: {
    id: 'GLOBAL.logIn',
    defaultMessage: 'Log in',
  },
  forgotPassword: {
    id: 'loginPage.forgotPassword',
    defaultMessage: 'Forgot your password?',
  },
  noAccount: {
    id: 'loginPage.noAccount',
    defaultMessage: "Don't have an account?",
  },
  register: {
    id: 'loginPage.register',
    defaultMessage: 'Register Now',
  },
  sessionExpired: {
    id: 'loginPage.sessionExpired',
    defaultMessage: 'Your session has expired.',
  },
  tempPasswordExpired: {
    id: 'loginPage.tempPasswordExpired',
    defaultMessage:
      'Your temporary password has expired, a new one has been emailed to you.',
  },
  passwordResetRequired: {
    id: 'loginPage.passwordResetRequired',
    defaultMessage: 'Password reset required.',
  },
  loggedOut: {
    id: 'loginPage.loggedOut',
    defaultMessage: 'You have successfully logged out.',
  },
  handoffFailed: {
    id: 'loginPage.handoffFailed',
    defaultMessage: 'Your sign-in link has expired. Please log in.',
  },
  invalidCredentials: {
    id: 'loginPage.invalidCredentials',
    defaultMessage: 'Invalid login credentials.',
  },
  serverError: {
    id: 'loginPage.serverError',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

function LoginPage() {
  const { formatMessage } = useIntl();

  const [message, setMessage] = useState<{
    type: 'error' | 'success';
    message: string;
  } | null>(null);

  const [reactivationDialog, setReactivationDialog] = useState<{
    openDialog: boolean;
    email: string;
  }>({
    openDialog: false,
    email: '',
  });
  const handleClose = () =>
    setReactivationDialog({ openDialog: false, email: '' });

  const emailParam = useSearchParam('email');
  const { login } = useSession();
  // Already logged in, redirect to dash
  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { state } = window.history;
    if (state) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (state.redirect === SESSION_EXPIRED) {
        setMessage({
          type: 'error',
          message: formatMessage(messages.sessionExpired),
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (state.redirect === LOGGED_OUT) {
        setMessage({
          type: 'success',
          message: formatMessage(messages.loggedOut),
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      } else if (state.redirect === HANDOFF_FAILED) {
        setMessage({
          type: 'error',
          message: formatMessage(messages.handoffFailed),
        });
      }
      window.history.replaceState({}, '');
    }

    // async function fetch() {
    //   const { data } = await checkSession();
    //   if (data?.checkSession) {
    //     initialize();
    //   }
    // }

    // fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Request login validation
  async function handleLoginSubmit({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) {
    setMessage(null);
    try {
      await login(email, password);
    } catch (error: unknown) {
      const errorCode = Array.isArray(error) ? error[0]?.code : undefined;
      const isServerError =
        !Array.isArray(error) || errorCode === 'INTERNAL_SERVER_ERROR';
      setMessage({
        type: 'error',
        message: formatMessage(
          isServerError ? messages.serverError : messages.invalidCredentials
        ),
      });
    }
  }

  return (
    <AuthLayout
      bottomContent={
        // <>
        //   <BottomText>
        //     {`${formatMessage(messages.noAccount)} `}
        //     <Link to={ROUTES.PLAN_MEMBERS}>
        //       {formatMessage(messages.register)}
        //     </Link>
        //   </BottomText>
        <BottomText>
          <Link to={ROUTES.FORGOT_PASSWORD}>
            {formatMessage(messages.forgotPassword)}
          </Link>
        </BottomText>
        // </>
      }
      title={formatMessage(messages.logIn)}
    >
      <LoginForm email={emailParam ?? ''} onSubmit={handleLoginSubmit} />

      {message ? (
        <InlineMessage message={message.message} type={message.type} />
      ) : null}

      <Dialog onClose={handleClose} open={reactivationDialog.openDialog}>
        <DisableEnableUserContent
          disabled={true}
          email={reactivationDialog.email}
          onSubmit={handleClose}
        />
      </Dialog>
    </AuthLayout>
  );
}

export default LoginPage;
